import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import classNames from "classnames";
import { translateBackendErrorCode } from "@jti/fe-common";
import { CasPurposesApi, SnpApi } from "@jti/rest-api";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";
import { AlertBanner } from "../../../../components/AlertBanner";
import { Button } from "../../../../components/Button";
import { Checkbox } from "../../../../components/Checkbox/Checkbox";
import { Form, FormField, useFieldArray, useForm } from "../../../../components/Form";
import { Recaptchabar } from "../../../../components/Recaptchabar";
import { Toaster } from "../../../../components/Toaster";
import { RichText } from "../../../../eva-components/RichText";
import { useCurrentBreakpoint } from "../../../../hooks/useCurrentBreakpoint";
import { stripHtmlTags } from "../../../../utils/stripHtmlTags/stripHtmlTags";
import { EmailInput } from "./components";
import styles from "./SubscribeForm.module.scss";
export const SubscribeForm = ({ description, emailInputProps, successMessage, languages, reCaptchaEnabled, brandingInformationEnabled, ...restProps }) => {
    const currentLanguage = useMemo(() => languages?.find(({ current }) => current) || null, [languages]);
    const [currentFormState, setCurrentFormState] = useState("clean");
    const form = useForm({
        defaultValues: {
            email: "",
            consents: [],
        },
    });
    const { formState, control } = form;
    const { fields, append } = useFieldArray({
        control,
        name: "consents",
    });
    useEffect(() => {
        async function getPurposeData() {
            const data = await CasPurposesApi.init().getPurposesByStep("subscription");
            append(data.map((consent) => ({ ...consent, checked: false })));
        }
        getPurposeData();
    }, []);
    const onSubmit = async () => {
        const { email, consents } = form.getValues();
        const payload = {
            email,
            topics: ["marketing"],
            preferredLanguage: currentLanguage?.associatedCookieValue || consents[0].language,
            data_processing_agreement: consents.every((checkbox) => checkbox.checked),
            consent_purposes: consents.map(({ id, checked: accepted }) => ({
                id,
                accepted,
            })),
        };
        try {
            dispatchTrackingEvent(TrackingEvents.TrackingSubscribeBlockClick);
            await SnpApi.init().subscribe(payload);
            setCurrentFormState("success");
        }
        catch (e) {
            const translatedError = translateBackendErrorCode(e?.error_code);
            const [error] = e.errors;
            toast.error(translatedError || error);
        }
    };
    const isSuccessFullySubmitted = currentFormState === "success";
    const { title, subTitle } = restProps;
    const { mainBreakpoint } = useCurrentBreakpoint();
    const buttonSize = mainBreakpoint === "xs" ? "small" : "medium";
    return (_jsxs("div", { className: classNames(styles.subscribeFormContainer, "subscribe-form-container"), children: [_jsx(Toaster, {}), _jsx("h3", { className: styles.subscribeFormTitle, children: title }), _jsxs("div", { className: styles.textContent, children: [_jsx("h4", { className: styles.subscribeFormSubTitle, children: subTitle }), _jsx(RichText, { className: styles.subscribeFormDescription, content: description })] }), isSuccessFullySubmitted && (_jsx(AlertBanner, { title: successMessage.title, variant: "success", description: successMessage.subTitle, onClose: () => setCurrentFormState("clean") })), _jsx(Form, { form: form, onSubmit: onSubmit, children: !isSuccessFullySubmitted && (_jsxs("div", { className: styles.formContainer, children: [_jsxs("div", { className: classNames(styles.submitSection, "submit-section"), children: [_jsx("div", { className: classNames(styles.inputContainer, "input-container"), children: _jsx(EmailInput, { ...emailInputProps }) }), _jsx(Button, { size: buttonSize, disabled: !formState.isValid, className: classNames(styles.submitBtn, "submit-btn"), "data-testid": "subscribeFormSubmit", type: "submit", children: "Subscribe" })] }), _jsx("div", { className: classNames(styles.formColumn, "form-column"), children: fields.map((consent, index) => {
                                const consentId = consent.id;
                                return (_jsx(FormField, { dataTestId: consentId, name: `consents.${index}`, validation: {
                                        validate: (value) => {
                                            return value.checked || `Consent for ${value.description} is required`;
                                        },
                                    }, showCustomError: true, render: ({ field }) => (_jsx(Checkbox, { "aria-label": stripHtmlTags(consent.description), labelHtml: `${consent.description}${consent.mandatory ? "*" : ""}`, ...field, isError: Boolean(formState.errors?.consents?.[index]), errorText: formState.errors?.consents?.[index] ? String(formState.errors?.consents?.[index].message) : "" })) }, consentId));
                            }) }), reCaptchaEnabled && brandingInformationEnabled && (_jsx("div", { className: styles.subscribeFormRecaptcha, children: _jsx(Recaptchabar, {}) }))] })) })] }));
};
//# sourceMappingURL=SubscribeForm.js.map